// src/salesAssessment/SalesAssessmentDashboard.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  FileCopy as CopyIcon,
  Person as PersonIcon,
  PlayArrow as ProcessIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderIndicator from '../layouts/TestReminderIndicator';
import { NotificationsActive as ReminderNowIcon } from '@mui/icons-material';
import useStore from '../config/store';
import api from '../config/api';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import SalesAssessmentReportModal from './SalesAssessmentReportModal';

const filterByName = createFilterOptions({
  stringify: (option) => `${option?.name || ''}`,
  trim: true,
});

const SalesAssessmentDashboard = () => {
  const { t } = useTranslation();
  const { authToken, user, project, organization, showAlert } = useStore();

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applicantFilter, setApplicantFilter] = useState(null);
  const [processing, setProcessing] = useState({});
  const [resending, setResending] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);

  // Estados para el modal de resultados
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  // Estados de check de puntos
  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  // Cargar tests
  const fetchTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(
        `/sales-assessments/by-project?projectId=${project}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response?.status_response === "OK") {
        setTests(data.response.data || []);
      } else {
        setError(t('salesAssessment.dashboard.errorLoading'));
      }
    } catch (error) {
      console.error("Error loading sales assessments:", error);
      setError(t('salesAssessment.dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project && authToken) {
      fetchTests();
    }
  }, [project, authToken]);

  // Verificar límites de uso para procesar
  useEffect(() => {
    const checkLimits = async () => {
      if (!organization || !authToken) return;

      try {
        setLoadingUsageCheck(true);
        const { data } = await api.get(
          `/organizations/${organization}/usage/check`,
          {
            params: { action: 'salesAssessment' },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (data.response) {
          setUsageCheck(data.response);
        }
      } catch (error) {
        console.error('Error checking usage limits:', error);
      } finally {
        setLoadingUsageCheck(false);
      }
    };

    checkLimits();
  }, [organization, authToken]);

  // SUSCRIPCIÓN A FIRESTORE - Escuchar tests en processing
  useEffect(() => {
    if (!tests || tests.length === 0) return;

    const unsubscribers = [];

    // Por cada test en processing, escuchar cambios
    tests.forEach(test => {
      if (test.status === 'processing') {
        const testRef = doc(db, 'sales_assessments', test.id);
        const unsubscribe = onSnapshot(testRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Si cambió el estado, recargar tabla
            if (data.status !== 'processing') {
              console.log(`✅ Test ${test.id} cambió de processing a ${data.status}`);
              fetchTests();
            }
          }
        });
        unsubscribers.push(unsubscribe);
      }
    });

    // Cleanup
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [tests]);

  // Obtener color según estado
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      sent: 'info',
      started: 'info',
      completed: 'success',
      processing: 'secondary',
      evaluated: 'success',
      processing_error: 'error',
      expired: 'default',
    };
    return colors[status] || 'default';
  };

  // Traducir estado
  const getStatusLabel = (status) => {
    return t(`salesAssessment.dashboard.statuses.${status}`, status);
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp?._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Verificar permisos para eliminar
  const canDeleteTest = () => {
    return ['super-admin', 'admin', 'admin-grafton'].includes(user?.rol);
  };

  // Obtener postulantes únicos
  const getUniqueApplicants = () => {
    const applicantsMap = new Map();
    tests.forEach(test => {
      if (test.applicantId && !applicantsMap.has(test.applicantId)) {
        applicantsMap.set(test.applicantId, {
          id: test.applicantId,
          name: test.applicantName,
          email: test.applicantEmail,
        });
      }
    });
    return Array.from(applicantsMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
  };

  const uniqueApplicants = getUniqueApplicants();

  // Filtrar tests
  const getFilteredTests = () => {
    if (!applicantFilter) {
      return tests;
    }
    return tests.filter(test => test.applicantId === applicantFilter.id);
  };

  const filteredTests = getFilteredTests();

  // Manejar procesamiento de test
  const handleProcessTest = async (testId) => {
    try {
      setProcessing(prev => ({ ...prev, [testId]: true }));
      const { data } = await api.post(
        `/sales-assessments/${testId}/process`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.errorCode === 0) {
        showAlert(t('salesAssessment.dashboard.testProcessing'), 'success');
        fetchTests();
      } else {
        showAlert(t('salesAssessment.dashboard.errorProcessing'), 'error');
      }
    } catch (error) {
      console.error('Error processing test:', error);
      if (error.response?.status === 409 &&
        error.response?.data?.response?.status_response === 'LIMIT_EXCEEDED') {
        showAlert(t('salesAssessment.usageCheck.limitReached'), 'error');
        setUsageCheck(prev => prev ? { ...prev, canPerform: false } : prev);
      } else {
        showAlert(t('salesAssessment.dashboard.errorProcessing'), 'error');
      }
    } finally {
      setProcessing(prev => ({ ...prev, [testId]: false }));
    }
  };

  // Manejar reenvío de test
  const handleResendTest = async (testId) => {
    try {
      setResending(prev => ({ ...prev, [testId]: true }));
      const { data } = await api.post(
        `/sales-assessments/${testId}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response?.status_response === 'OK') {
        showAlert(t('salesAssessment.dashboard.testResentSuccess'), 'success');
        fetchTests();
      } else {
        showAlert(t('salesAssessment.dashboard.errorResending'), 'error');
      }
    } catch (error) {
      console.error("Error resending test:", error);
      showAlert(t('salesAssessment.dashboard.errorResending'), 'error');
    } finally {
      setResending(prev => ({ ...prev, [testId]: false }));
    }
  };

  // Copiar URL del test
  const [sendingReminder, setSendingReminder] = useState({});

  // Manda el correo de recordatorio en el acto y reinicia el reloj: el proximo
  // recordatorio automatico se cuenta desde ahora. Distinto de "reenviar", que
  // vuelve a mandar el correo original del test.
  const handleSendReminderNow = async (test) => {
    setSendingReminder((prev) => ({ ...prev, [test.id]: true }));
    try {
      await api.post(
        `/sales-assessments/${test.id}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      showAlert(t('testReminder.reminderSent'), 'success');
      fetchTests();
    } catch (err) {
      console.error('Error sending reminder:', err);
      showAlert(err.response?.data?.errorMessage || t('testReminder.reminderError'), 'error');
    } finally {
      setSendingReminder((prev) => ({ ...prev, [test.id]: false }));
    }
  };

  // Copiar el enlace es el momento en que el consultor lo envia por su cuenta,
  // asi que el test pasa de "pendiente" a "enviado" y queda habilitado el reenvio.
  // No agenda recordatorio: eso solo ocurre cuando el correo lo manda el sistema,
  // porque si no el recordatorio saldria de un remitente que el candidato no vio.
  const handleCopyTestUrl = async (test) => {
    const testUrl = `${window.location.origin}/public/sales-assessment/${test.id}`;

    try {
      await navigator.clipboard.writeText(testUrl);
    } catch (err) {
      console.error('Error copying URL:', err);
      showAlert(t('salesAssessment.dashboard.urlCopyError'), 'error');
      return;
    }

    if (test.status !== 'pending') {
      showAlert(t('salesAssessment.dashboard.urlCopied'), 'success');
      return;
    }

    try {
      await api.post(
        `/sales-assessments/${test.id}/mark-as-sent`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      showAlert(t('testReminder.urlCopiedAndMarked'), 'success');
      fetchTests();
    } catch (err) {
      console.error('Error marking test as sent:', err);
      showAlert(t('salesAssessment.dashboard.urlCopied'), 'success');
    }
  };

  // Abrir modal de eliminación
  const handleOpenDeleteDialog = (test) => {
    setTestToDelete(test);
    setDeleteDialogOpen(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteDialog = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setTestToDelete(null);
    }
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!testToDelete) return;

    try {
      setDeleting(true);
      const { data } = await api.delete(
        `/sales-assessments/${testToDelete.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response?.status_response === 'OK') {
        showAlert(t('salesAssessment.dashboard.testDeletedSuccess'), 'success');
        fetchTests();
        handleCloseDeleteDialog();
      } else {
        showAlert(t('salesAssessment.dashboard.errorDeleting'), 'error');
      }
    } catch (error) {
      console.error("Error deleting test:", error);
      showAlert(t('salesAssessment.dashboard.errorDeleting'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Ver reporte (abre modal)
  const handleViewReport = (test) => {
    setSelectedTest(test);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (tests.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {t('salesAssessment.dashboard.noTestsFound')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('salesAssessment.dashboard.createFirstTest')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Alert de puntos insuficientes */}
      {!loadingUsageCheck && usageCheck && !usageCheck.canPerform && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            {usageCheck.reason === 'SERVICE_NOT_AVAILABLE'
              ? t('salesAssessment.usageCheck.serviceNotAvailable')
              : t('salesAssessment.usageCheck.limitReached')
            }
          </Typography>
          {usageCheck.message && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {usageCheck.message}
            </Typography>
          )}
          {usageCheck.remaining !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {t('salesAssessment.usageCheck.pointsAvailable', { points: usageCheck.remaining })}
            </Typography>
          )}
        </Alert>
      )}

      {/* Filtros y acciones */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, gap: 2 }}>
        <Autocomplete
          options={uniqueApplicants}
          getOptionLabel={(option) => option?.name || ''}
          getOptionKey={(option) => option.id ?? option.email}
          isOptionEqualToValue={(option, value) =>
            (option.id ?? option.email) === (value.id ?? value.email)
          }
          filterOptions={filterByName}
          value={applicantFilter}
          onChange={(event, newValue) => setApplicantFilter(newValue)}
          sx={{ minWidth: 300 }}
          noOptionsText={t('noData')}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('salesAssessment.dashboard.searchApplicant')}
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <PersonIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id ?? option.email}>
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                </Typography>
              </Box>
            </li>
          )}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTests}
        >
          {t('salesAssessment.dashboard.refresh')}
        </Button>
      </Box>

      {/* Tabla de tests */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('salesAssessment.dashboard.applicant')}</TableCell>
              <TableCell>{t('salesAssessment.dashboard.position')}</TableCell>
              <TableCell>{t('salesAssessment.dashboard.createdBy')}</TableCell>
              <TableCell>{t('salesAssessment.dashboard.status')}</TableCell>
              <TableCell>{t('salesAssessment.dashboard.sentDate')}</TableCell>
              <TableCell>{t('salesAssessment.dashboard.completedDate')}</TableCell>
              <TableCell align="center">{t('salesAssessment.dashboard.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTests.map((test) => (
              <TableRow key={test.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {test.applicantName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {test.applicantEmail}
                  </Typography>
                </TableCell>
                <TableCell>
                  {test.positionData?.name || t('salesAssessment.dashboard.notSpecified')}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {test.userEmail || t('salesAssessment.dashboard.notAvailable')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(test.status)}
                    color={getStatusColor(test.status)}
                    size="small"
                  />
                  <TestReminderIndicator test={test} />
                </TableCell>
                <TableCell>{formatDate(test.sentAt)}</TableCell>
                <TableCell>
                  {test.completedAt ? formatDate(test.completedAt) : '-'}
                </TableCell>
                <TableCell align="center">
                  {/* Botón PROCESAR - Solo si está completed */}
                  {test.status === 'completed' && (
                    <Tooltip title={usageCheck && !usageCheck.canPerform
                      ? t('salesAssessment.usageCheck.limitReached')
                      : t('salesAssessment.dashboard.processTest')
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleProcessTest(test.id)}
                          disabled={processing[test.id] || (usageCheck && !usageCheck.canPerform)}
                          color="primary"
                        >
                          {processing[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ProcessIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón VER - Solo si está evaluated */}
                  {(test.status === 'processing' || test.status === 'evaluated') && (
                    <Tooltip title={test.status === 'processing' ? t('salesAssessment.dashboard.processing') : t('salesAssessment.dashboard.viewReport')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleViewReport(test)}
                          disabled={test.status === 'processing'}
                          color="secondary"
                        >
                          {test.status === 'processing' ? (
                            <CircularProgress size={20} />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón REPROCESAR - Solo para super-admin en tests evaluated */}
                  {test.status === 'evaluated' && user.rol === 'super-admin' && (
                    <Tooltip title={usageCheck && !usageCheck.canPerform
                      ? t('salesAssessment.usageCheck.limitReached')
                      : t('salesAssessment.dashboard.reprocess')
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleProcessTest(test.id)}
                          disabled={processing[test.id] || (usageCheck && !usageCheck.canPerform)}
                          color="warning"
                        >
                          {processing[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReplayIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón REINTENTAR - Solo si hay error */}
                  {test.status === 'processing_error' && (
                    <Tooltip title={usageCheck && !usageCheck.canPerform
                      ? t('salesAssessment.usageCheck.limitReached')
                      : (test.processingError || t('salesAssessment.dashboard.retry'))
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleProcessTest(test.id)}
                          disabled={processing[test.id] || (usageCheck && !usageCheck.canPerform)}
                          color="warning"
                        >
                          {processing[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReplayIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón COPIAR URL */}
                  <Tooltip title={t('salesAssessment.dashboard.copyUrl')}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyTestUrl(test)}
                      color="info"
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Boton ENVIAR TEST: solo mientras el test no ha salido */}
                  {test.status === 'pending' && (
                    <Tooltip title={t('salesAssessment.sendTest')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleResendTest(test.id)}
                          disabled={!!resending[test.id]}
                          color="secondary"
                        >
                          {resending[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SendIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Boton RECORDAR: envio inmediato del recordatorio */}
                  {test.status === 'sent' && (
                    <Tooltip title={t('testReminder.sendReminderNow')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleSendReminderNow(test)}
                          disabled={!!sendingReminder[test.id]}
                          color="primary"
                        >
                          {sendingReminder[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReminderNowIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón ELIMINAR */}
                  <Tooltip title={t('salesAssessment.dashboard.deleteTest')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(test)}
                      color="error"
                      disabled={!canDeleteTest()}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal para ver resultados */}
      <SalesAssessmentReportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        test={selectedTest}
      />

      {/* Modal de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('salesAssessment.dashboard.deleteDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('salesAssessment.dashboard.deleteConfirmation')} <strong>"{testToDelete?.applicantName}"</strong>?
          </Typography>
          {testToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('salesAssessment.dashboard.applicant')}:</strong> {testToDelete.applicantName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('salesAssessment.dashboard.position')}:</strong> {testToDelete.positionData?.name || t('salesAssessment.dashboard.notSpecified')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('salesAssessment.dashboard.status')}:</strong> {getStatusLabel(testToDelete.status)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error">
            {t('salesAssessment.dashboard.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleting}
          >
            {t('salesAssessment.dashboard.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : t('salesAssessment.dashboard.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesAssessmentDashboard;